import {AnnotationCommand} from 'substance'

class DWAnnotationCommand extends AnnotationCommand {
    /**
     * Check whether a selection allows annotation tools to be shown or not
     *
     * @param  {PropertySelection} sel A selection in a surface
     * @return {Boolean}
     */
    isDisabled(sel) {
        if (!sel || !sel.surfaceId || sel.isNodeSelection() || sel.isNull() || sel.isCollapsed()) {
            return true
        }

        if (sel.surfaceId === 'body' || sel.surfaceId.indexOf('body/') === 0) {
            const disabledPlugins = this._hasAnnotationsDisabled(sel)
            if (disabledPlugins.length) {
                return true
            }
        }

        return false
    }

    /**
     * Override execute in Annotation to allow executing this command
     * over multiple text blocks. This will effectively create one transaction
     * per text block and so result in multiple undos to restore.
     *
     * @param {Object} params Info object with execution details
     */
    execute(params) {
        const sel = params.selection
        if (!sel.isContainerSelection()) {
            return super.execute(params)
        }
        else {
            return this.executeMultipe(params)
        }
    }

    /**
     * Execute a command on multiple text nodes as separate transactions and then
     * restore the originating selection.
     *
     * @param {Object} params
     */
    executeMultipe(params) {
        const sel = params.selection
        const start = sel.start
        const end = sel.end
        const doc = params.selection.getDocument()
        const editorSession = this._getEditorSession(params)
        const nodeIds = doc.getNodes()['body'].nodes

        let inContainerSelection = false
        nodeIds.forEach(nodeId => {
            const node = doc.get(nodeId)
            if (!node.isText()) {
                return
            }

            if (nodeId === start.path[0]) {
                inContainerSelection = true
            }

            if (inContainerSelection) {
                let startOffset = nodeId === start.path[0] ? start.offset : 0
                let endOffset = nodeId === end.path[0] ? end.offset : node.getLength()
                this.executeCommandOnNode(params, doc, editorSession, node, startOffset, endOffset)
            }

            if (nodeId === end.path[0]) {
                inContainerSelection = false
            }
        })

        // Restore selection
        editorSession.setSelection(
            doc.createSelection({
                type: 'container',
                containerId: params.surface.containerId,
                startOffset: start.offset,
                endOffset: end.offset,
                startPath: start.path,
                endPath: end.path
            })
        )
    }

    /**
     * Set specified selection on a node and execute a command
     *
     * @param {object} params
     * @param {object} doc
     * @param {object} editorSession
     * @param {object} node
     * @param {integer} startOffset
     * @param {integer} endOffset
     */
    executeCommandOnNode(params, doc, editorSession, node, startOffset, endOffset) {
        editorSession.setSelection(
            doc.createSelection({
                type: 'property',
                containerId: params.surface.containerId,
                path: [node.id, 'content'],
                startOffset: startOffset,
                endOffset: endOffset
            })
        )

        editorSession.executeCommand(this.name)
    }

    /**
     * Investigate whether selection spans text styles or fields which
     * have disabled annotations.
     *
     * Does not handle selections in table cells
     * (Does not handle the (future!) case where a plugin can be added multiple
     * times with different configurations. Ie where you need to investigate and
     * compare with a specific plugin id as well as the plugin name.)
     *
     * @param  {PropertySelection} sel A selection in a surface
     * @return {Array} An array of plugins with annotations disabled
     */
    _hasAnnotationsDisabled(sel) {
        const nodeTypes = this._getNodeTypesInSelection(sel)

        // TODO: No more PluginManager
        const pluginPackages = writer.api.configurator.getWriterConfigValue('plugins').filter((pluginPackage) => {
            return nodeTypes.includes(pluginPackage.name)
        })

        return pluginPackages.filter(pp => {
            const data = pp.data
            if (!data) {
                return false
            }

            // Check if completely disabled
            if (data.disableUseOfAnnotationTools === true) {
                return true
            }

            // Disable on container selections
            if (sel.type === 'container') {
                // FIXME: Investigate further if the container selection spans text elements
                return false
            }

            // Check specific fields and compare with current selection
            if (Array.isArray(sel.path) && sel.path.length > 1) {
                if (Array.isArray(data.disableUseOfAnnotationToolsForFields)) {
                    if (data.disableUseOfAnnotationToolsForFields.indexOf(sel.path[1]) >= 0) {
                        return true
                    }
                }
            }

            return false
        })
    }

    /**
     * Find all unique node types from an array of node ids, iteratively
     * traverses parents if applicable (i e in table-cell -> table)
     *
     * @param  {PropertySelection} sel A selection in a surface
     * @param  {Array} nodeIds Array of node ids to investigate
     * @return {Array} Array of strings, node types
     */
    _getNodeTypes(sel, nodeIds) {
        let nodeTypes = []
        for(let nodeId of nodeIds) {
            const node = sel.getDocument().get(nodeId)
            if (!nodeTypes.includes(node.type)) {
                nodeTypes.push(node.type)
            }

            if (node.parent) {
                // Concat and uniquify
                nodeTypes = [
                    ...new Set([
                        ...nodeTypes,
                        ...this._getNodeTypes(sel, [node.parent])
                    ])
                ]
            }
        }

        return nodeTypes
    }

    /**
     * Find all unique node types in a selection
     *
     * @param  {PropertySelection} sel A selection in a surface
     * @return {Array} Array of strings, node types
     */
    _getNodeTypesInSelection(sel) {
        let nodeIds = []
        if (sel.getNodeId) {
            nodeIds.push(sel.getNodeId())
        }
        else {
            nodeIds = sel.getNodeIds()
        }

        return this._getNodeTypes(sel, nodeIds)
    }
}

export {DWAnnotationCommand}
